import { useState, useEffect, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Lock, AlertCircle, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { logger } from '../../lib/logger';

export function ResetPassword() {
  const { updatePassword } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [validToken, setValidToken] = useState(false);

  useEffect(() => {
    // Check if we have a recovery token in the URL
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const type = hashParams.get('type');

    if (accessToken && type === 'recovery') {
      setValidToken(true);
    } else {
      setError('Enlace de recuperación inválido o expirado.');
    }
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validation
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      await updatePassword(password);
      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err: any) {
      logger.error('[ResetPassword] Error:', err);
      setError(err.message || 'Error al actualizar la contraseña. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  if (!validToken && !error) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md p-8 bg-white rounded-2xl shadow-2xl text-center"
      >
        <div className="w-16 h-16 border-4 border-[#5D1828] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-[#6B7280]">Verificando enlace...</p>
      </motion.div>
    );
  }

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md p-8 bg-white rounded-2xl shadow-2xl text-center"
      >
        <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 size={40} className="text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-[#111827] mb-3">
          ¡Contraseña actualizada!
        </h2>
        <p className="text-[#6B7280] mb-6">
          Tu contraseña ha sido cambiada exitosamente.
        </p>
        <p className="text-sm text-[#6B7280]">
          Redirigiendo al inicio de sesión...
        </p>
      </motion.div>
    );
  }

  if (error && !validToken) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md p-8 bg-white rounded-2xl shadow-2xl text-center"
      >
        <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
          <AlertCircle size={40} className="text-red-600" />
        </div>
        <h2 className="text-2xl font-bold text-[#111827] mb-3">
          Enlace inválido
        </h2>
        <p className="text-[#6B7280] mb-6">{error}</p>
        <Link
          to="/forgot-password"
          className="inline-block px-6 py-3 bg-[#5D1828] text-white rounded-lg font-medium hover:bg-[#7A2235] transition-colors shadow-lg shadow-[#5D1828]/30"
        >
          Solicitar nuevo enlace
        </Link>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-md p-8 bg-white rounded-2xl shadow-2xl"
    >
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gradient-to-br from-[#5D1828] to-[#7A2235] rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Lock size={32} className="text-white" />
        </div>
        <h1 className="text-3xl font-bold text-[#111827] mb-2">
          Nueva contraseña
        </h1>
        <p className="text-[#6B7280]">
          Ingresa tu nueva contraseña
        </p>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 text-red-700 text-sm"
        >
          <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </motion.div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-[#374151] mb-2">
            Nueva contraseña
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={20} />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-[#D1D5DB] rounded-lg focus:ring-2 focus:ring-[#5D1828] focus:border-transparent transition-all"
              placeholder="••••••••"
              required
              disabled={loading}
              minLength={6}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-[#374151] mb-2">
            Confirmar contraseña
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={20} />
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-[#D1D5DB] rounded-lg focus:ring-2 focus:ring-[#5D1828] focus:border-transparent transition-all"
              placeholder="••••••••"
              required
              disabled={loading}
              minLength={6}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-[#5D1828] text-white rounded-lg font-medium hover:bg-[#7A2235] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-[#5D1828]/30 flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Actualizando...
            </>
          ) : (
            <>
              <Lock size={20} />
              Actualizar contraseña
            </>
          )}
        </button>
      </form>
    </motion.div>
  );
}
